import React, { useCallback, useEffect, useState } from "react";
import { Tabs, Typography, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  getMyDocuments,
  uploadMyDocument,
  getCareTeamDocuments,
  downloadBloodWork,
} from "../../../redux/patientSlice";
import DocumentTable from "../../Documents/DocumentTable";
import DocumentUploadForm from "../../Documents/DocumentUploadForm";

const { Title, Paragraph } = Typography;

const mapDoc = (file) => ({
  fileRef: file.fileRef,
  title: file.fileTitle,
  filename: file.filename,
  dateAdded: file.createdOn,
  providerName: file.providerName,
  providerProfilePhoto: file.providerProfilePhoto,
  providerInitials: file.providerInitials,
});

const getFileMime = (filename) => {
  const extension = filename?.split(".").pop()?.toLowerCase();
  if (extension === "png") return "image/png";
  if (["jpg", "jpeg"].includes(extension)) return "image/jpeg";
  return "application/pdf";
};

const PatientDocuments = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("careTeam");
  const [view, setView] = useState("list"); // "list" | "upload"
  const [careTeamDocs, setCareTeamDocs] = useState([]);
  const [myDocs, setMyDocs] = useState([]);
  const [loadingCareTeam, setLoadingCareTeam] = useState(false);
  const [loadingMine, setLoadingMine] = useState(false);

  const fetchCareTeamDocuments = useCallback(async () => {
    setLoadingCareTeam(true);
    const result = await dispatch(getCareTeamDocuments());
    if (getCareTeamDocuments.fulfilled.match(result)) {
      setCareTeamDocs((result.payload || []).map(mapDoc));
    } else {
      // Endpoint not built yet on the backend — show an honest empty state.
      setCareTeamDocs([]);
    }
    setLoadingCareTeam(false);
  }, [dispatch]);

  const fetchMyDocuments = useCallback(async () => {
    setLoadingMine(true);
    const result = await dispatch(getMyDocuments());
    if (getMyDocuments.fulfilled.match(result)) {
      setMyDocs((result.payload || []).map(mapDoc));
    } else {
      message.error("Failed to load your documents.");
    }
    setLoadingMine(false);
  }, [dispatch]);

  useEffect(() => {
    fetchCareTeamDocuments();
    fetchMyDocuments();
  }, [fetchCareTeamDocuments, fetchMyDocuments]);

  const handleDownload = async (fileRef, filename) => {
    const result = await dispatch(downloadBloodWork(fileRef));
    if (downloadBloodWork.fulfilled.match(result)) {
      const blob = new Blob([result.payload], { type: getFileMime(filename) });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || fileRef;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      message.error("Failed to download document.");
    }
  };

  const handleUpload = async ({ title, base64, filename }) => {
    const result = await dispatch(
      uploadMyDocument({ base64, filename, fileTitle: title })
    );
    if (uploadMyDocument.fulfilled.match(result)) {
      message.success("Document uploaded successfully.");
      setView("list");
      fetchMyDocuments();
    } else {
      message.error("Failed to upload document.");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ color: "#335CAD", letterSpacing: 0.5 }}>DOCUMENTS</Title>
      <Paragraph style={{ color: "#595959" }}>
        View and download important files shared by your care team, and upload any documents you'd like them to review.
      </Paragraph>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setView("list");
        }}
        items={[
          {
            key: "careTeam",
            label: "Care Team Documents",
            children: (
              <DocumentTable
                documents={careTeamDocs}
                loading={loadingCareTeam}
                showProvider
                onDownload={handleDownload}
                emptyText="No documents shared with you yet."
              />
            ),
          },
          {
            key: "mine",
            label: "My Documents",
            children:
              view === "upload" ? (
                <DocumentUploadForm
                  backLabel="Back to My Documents"
                  onCancel={() => setView("list")}
                  onSubmit={handleUpload}
                />
              ) : (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ paddingLeft: 0, marginBottom: 12 }}
                    onClick={() => setView("upload")}
                  >
                    Upload Document
                  </Button>
                  <DocumentTable
                    documents={myDocs}
                    loading={loadingMine}
                    onDownload={handleDownload}
                    emptyText="You haven't uploaded any documents yet."
                  />
                </>
              ),
          },
        ]}
      />
    </div>
  );
};

export default PatientDocuments;
